import { useQuery } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { CustomPageContext, PageTabs } from '~/app/Layout';
import { ROUTE } from '~/app/routes';
import { InlineCopyInput, Loading, PageHeader } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardTransaction, CardTransactionRepository } from '~/repositories/CardTransactionRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

interface Params {
  id: string;
}

export interface CardTransactionProps {
  cardTransaction: CardTransaction;
}

export const PageCardTransaction: FC<CustomPageContext> = ({ entries }) => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();
  const { id } = useParams<keyof Params>() as Params;

  const {
    data: cardTransaction,
    isLoading,
    error: cardTransactionError,
  } = useQuery({
    queryKey: ['card-transaction', id, currentPlatform?.id, isSandbox],
    queryFn: () => CardTransactionRepository.get(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (cardTransactionError) {
      navigate(ROUTE.CARD_TRANSACTIONS);
      addDangerNotification({
        content: (cardTransactionError as any).message || 'Failed to load card transaction',
      });
    }
  }, [cardTransactionError, navigate, addDangerNotification]);

  const outletContext = useMemo(() => (cardTransaction ? { cardTransaction } : undefined), [cardTransaction]);

  return (
    <Loading isLoading={isLoading} fullHeight>
      <PageHeader
        text="Card Transactions"
        backPath={ROUTE.CARD_TRANSACTIONS}
        tabs={<PageTabs entries={entries} />}
        backButton
      >
        {id && cardTransaction && (
          <InlineCopyInput label="Card Transaction ID" value={cardTransaction.id} minWidth="300px" isMonospaced />
        )}
      </PageHeader>
      {outletContext && <Outlet context={outletContext} />}
    </Loading>
  );
};
